type SegmentConnectivity = {
  street_segment_id: string
  source_node_id: string
  destination_node_id: string
  is_blocked: boolean
  is_bidirectional: boolean
}

function buildUnblockedNodeAdjacency(segments: Array<SegmentConnectivity>): Map<string, string[]> {
  const adjacency = new Map<string, string[]>()

  const addEdge = (from: string, to: string) => {
    if (!adjacency.has(from)) adjacency.set(from, [])
    if (!adjacency.has(to)) adjacency.set(to, [])
    adjacency.get(from)!.push(to)
  }

  for (const segment of segments) {
    if (segment.is_blocked) continue
    addEdge(segment.source_node_id, segment.destination_node_id)
    if (segment.is_bidirectional) {
      addEdge(segment.destination_node_id, segment.source_node_id)
    }
  }

  return adjacency
}

/**
 * Tarjan SCC over the unblocked directed graph (bidirectional segments yield both arcs).
 * Iterative traversal — planning areas can contain node chains deep enough to overflow
 * the call stack with the recursive formulation.
 */
function findStronglyConnectedComponents(adjacency: Map<string, string[]>): Array<Set<string>> {
  let index = 0
  const stack: string[] = []
  const onStack = new Set<string>()
  const indices = new Map<string, number>()
  const lowlinks = new Map<string, number>()
  const components: Array<Set<string>> = []

  const strongConnect = (start: string) => {
    const work: Array<{ node: string; edgeIndex: number }> = [{ node: start, edgeIndex: 0 }]

    while (work.length > 0) {
      const frame = work[work.length - 1]!
      const node = frame.node

      if (frame.edgeIndex === 0) {
        indices.set(node, index)
        lowlinks.set(node, index)
        index += 1
        stack.push(node)
        onStack.add(node)
      }

      const neighbors = adjacency.get(node) ?? []
      let descended = false
      while (frame.edgeIndex < neighbors.length) {
        const neighbor = neighbors[frame.edgeIndex]!
        frame.edgeIndex += 1
        if (!indices.has(neighbor)) {
          work.push({ node: neighbor, edgeIndex: 0 })
          descended = true
          break
        }
        if (onStack.has(neighbor)) {
          lowlinks.set(node, Math.min(lowlinks.get(node)!, indices.get(neighbor)!))
        }
      }
      if (descended) continue

      work.pop()
      if (lowlinks.get(node) === indices.get(node)) {
        const component = new Set<string>()
        let current: string
        do {
          current = stack.pop()!
          onStack.delete(current)
          component.add(current)
        } while (current !== node)
        components.push(component)
      }
      const parent = work[work.length - 1]
      if (parent) {
        lowlinks.set(parent.node, Math.min(lowlinks.get(parent.node)!, lowlinks.get(node)!))
      }
    }
  }

  for (const node of adjacency.keys()) {
    if (!indices.has(node)) strongConnect(node)
  }

  return components
}

/**
 * Keeps only segments whose both endpoints lie in the largest SCC of the unblocked network.
 * One-way dead-ends and islands outside that core are excluded from routing / display.
 */
export function computeSegmentsConnectedToUnblockedNetwork(segments: Array<SegmentConnectivity>): Set<string> {
  const adjacency = buildUnblockedNodeAdjacency(segments)
  const components = findStronglyConnectedComponents(adjacency)

  if (components.length === 0) return new Set()

  const largestComponent = components.reduce((largest, current) => (current.size > largest.size ? current : largest))

  const connected = new Set<string>()
  for (const segment of segments) {
    if (largestComponent.has(segment.source_node_id) && largestComponent.has(segment.destination_node_id)) {
      connected.add(segment.street_segment_id)
    }
  }

  return connected
}

/** Endpoints of segments already filtered to the routable (largest-SCC) network. */
export function collectRoutableNodeIds(
  segments: Array<{ source_node_id: string; destination_node_id: string }>
): Set<string> {
  const ids = new Set<string>()
  for (const segment of segments) {
    ids.add(segment.source_node_id)
    ids.add(segment.destination_node_id)
  }
  return ids
}
